import { useState } from 'react';
import type { Project } from '../lib/types';

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock projects data
  const projects: Project[] = [
    {
      id: '1',
      name: 'E-Commerce Platform',
      code: 'ECOM',
      owners: ['john@example.com', 'jane@example.com'],
      envs: ['production', 'staging', 'development'],
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-15')
    },
    {
      id: '2',
      name: 'Mobile API Gateway',
      code: 'MOBAPI',
      owners: ['mike@example.com'],
      envs: ['production', 'staging'],
      createdAt: new Date('2024-02-01'),
      updatedAt: new Date('2024-02-10')
    },
    {
      id: '3',
      name: 'Analytics Engine',
      code: 'ANALYTICS',
      owners: ['sarah@example.com', 'tom@example.com'],
      envs: ['production', 'development'],
      createdAt: new Date('2024-03-01'),
      updatedAt: new Date('2024-03-12')
    }
  ];

  if (selectedProject) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Project Detail Header */}
          <div className="mb-6">
            <button
              onClick={() => setSelectedProject(null)}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Projects
            </button>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedProject.name}</h1>
                  <p className="text-gray-600">Project Code: {selectedProject.code}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    <i className="ri-edit-line mr-2"></i>
                    Edit
                  </button>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    <i className="ri-settings-line mr-2"></i>
                    Configure
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6 mt-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Owners</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.owners.map(owner => (
                      <span key={owner} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                        {owner}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Environments</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.envs.map(env => (
                      <span key={env} className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                        {env}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                  <p className="text-gray-900">{selectedProject.updatedAt.toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Project Tabs */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="border-b border-gray-200 px-6">
              <nav className="flex space-x-8">
                {['Overview', 'Servers', 'Components', 'Dependencies', 'Configuration'].map((tab) => (
                  <button
                    key={tab}
                    className="py-4 px-1 border-b-2 border-blue-500 text-blue-600 font-medium text-sm"
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              <div className="text-center py-12">
                <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Project Overview</h3>
                <p className="text-gray-600">Details about {selectedProject.name} will be displayed here</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
            <p className="text-gray-600">Manage your infrastructure projects</p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <i className="ri-add-line mr-2"></i>
            Create Project
          </button>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map(project => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-folder-line text-blue-600 text-2xl"></i>
                </div>
                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  {project.code}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">{project.name}</h3>

              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-600">
                  <i className="ri-team-line mr-2"></i>
                  <span>{project.owners.length} owners</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <i className="ri-server-line mr-2"></i>
                  <span>{project.envs.length} environments</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <i className="ri-calendar-line mr-2"></i>
                  <span>Updated {project.updatedAt.toLocaleDateString()}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="text-xs text-gray-600">All systems healthy</span>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Project Modal (simplified) */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Create New Project</h2>
            <p className="text-gray-600 mb-4">Project creation form will be here</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
